// src/lib/ugcTypes.ts
//
// Types partagés du portail créateurs UGC (API et interfaces). Les vues composées du
// portail et de l'admin sont ajoutées ici par les tâches qui les produisent.

import type { UgcPlatform } from '../../functions/lib/ugc/links'
import type { ThreadItem } from '../../functions/lib/ugc/thread'

export type { UgcPlatform }

export type FollowersBand = 'lt_1k' | '1k_5k' | '5k_20k' | '20k_100k' | 'gt_100k'

/** Tranches d'abonnés ; la borne basse sert d'abonnés dans les réseaux du contact CRM. */
export const FOLLOWERS_BANDS: readonly { value: FollowersBand; label: string; lowerBound: number }[] = [
  { value: 'lt_1k', label: 'Moins de 1 000', lowerBound: 0 },
  { value: '1k_5k', label: '1 000 à 5 000', lowerBound: 1000 },
  { value: '5k_20k', label: '5 000 à 20 000', lowerBound: 5000 },
  { value: '20k_100k', label: '20 000 à 100 000', lowerBound: 20000 },
  { value: 'gt_100k', label: 'Plus de 100 000', lowerBound: 100000 },
]

export type ApplicationStatus = 'pending' | 'accepted' | 'rejected'
export type MemberStatus = 'invited' | 'active' | 'paused' | 'left'
export type VideoStatus =
  | 'idea_submitted'
  | 'idea_changes_requested'
  | 'idea_approved'
  | 'draft_submitted'
  | 'draft_changes_requested'
  | 'approved'
  | 'rejected'
export type DraftStatus = 'uploading' | 'ready' | 'aborted' | 'purged'
export type ReviewStage = 'idea' | 'draft'
export type ReviewDecision = 'approved' | 'changes_requested' | 'rejected'
export type PublicationStatus = 'live' | 'screenshot_submitted' | 'reading_validated'
export type ReadingSource = 'screenshot' | 'manual' | 'api'
export type SettlementKind = 'invoice' | 'arrangement'
export type BonusStatus = 'pending' | 'earned'
export type PayState = 'none' | 'due' | 'invoice_received' | 'paid' | 'settled'
/** Effet d'une correction de relevé sur le parrainage de la personne (spec § 7). */
export type CorrectionReferral = 'unchanged' | 'reverted' | 'earned' | 'settled'

/** Modification d'un membre par l'admin (corps de PATCH /api/ugc/members/:id, memberPatchSchema). */
export type MemberPatch = {
  status?: MemberStatus | undefined
  canInvoice?: boolean | undefined
  email?: string | undefined
  referredByMemberId?: string | null | undefined
  /** Dernier jour du Pro accordé dans RevenueCat (AAAA-MM-JJ), `null` pour l'effacer. */
  proUntil?: string | null | undefined
  /** Classe la demande de Pro en attente (compte introuvable dans RevenueCat…). */
  dismissProRequest?: true | undefined
}

export type UgcApplication = {
  id: string
  appId: string
  displayName: string
  email: string
  tiktokHandle: string | null
  tiktokFollowers: FollowersBand | null
  instagramHandle: string | null
  instagramFollowers: FollowersBand | null
  audienceFrance: boolean
  canInvoice: boolean
  referralCode: string | null
  referrerMemberId: string | null
  status: ApplicationStatus
  decidedAt: string | null
  decisionMessage: string | null
  memberId: string | null
  createdAt: string
}

export type UgcMember = {
  id: string
  appId: string
  creatorId: string
  email: string
  displayName: string
  status: MemberStatus
  canInvoice: boolean
  referralCode: string
  referredByMemberId: string | null
  consentedAt: string | null
  lastLoginAt: string | null
  createdAt: string
  updatedAt: string
  /** E-mails des messages de l'équipe : un à chaque message (`each`) ou un résumé le matin (`daily`). */
  emailDigest: EmailDigest
  /** Adresse du compte dans l'app Basalt Lab, donnée par la personne pour recevoir le Pro (migration 0019). */
  appAccountEmail: string | null
  /** Heure où la personne a donné ou changé cette adresse. */
  proRequestedAt: string | null
  /** Dernier jour du Pro accordé par l'équipe (AAAA-MM-JJ), ou `null`. */
  proUntil: string | null
  /** Heure où l'équipe a classé la demande (compte introuvable…) ; une demande plus récente la relance. */
  proDismissedAt: string | null
  /** Adresse d'avant le dernier changement, et la date de ce changement. */
  previousAppAccountEmail: string | null
  appAccountChangedAt: string | null
  /**
   * Montant pour 1 000 vues fixé par l'équipe pour cette personne, en centimes (migration 0021) ;
   * `null` tant qu'il n'est pas fixé : aucun relevé ne se valide, l'espace affiche « En cours
   * d'analyse du montant attribuable ».
   */
  rateCentsPerThousand: number | null
  /** Dernière modification de ce montant par l'équipe. */
  rateSetAt: string | null
}

export type UgcVideo = {
  id: string
  memberId: string
  title: string
  idea: string
  hook: string
  status: VideoStatus
  approvedAt: string | null
  createdAt: string
  updatedAt: string
  /** Dernière modification de l'idée avant relecture, ou `null` (l'équipe le voit dans sa file). */
  ideaEditedAt: string | null
}

export type UgcDraft = {
  id: string
  videoId: string
  version: number
  filename: string
  contentType: string
  size: number
  status: DraftStatus
  createdAt: string
  completedAt: string | null
  /** Retirée par la personne : fichier effacé (`status` à `purged`), le numéro reste au fil. */
  withdrawnAt: string | null
}

export type UgcReview = {
  id: string
  videoId: string
  draftId: string | null
  stage: ReviewStage
  decision: ReviewDecision
  comment: string | null
  createdAt: string
}

export type UgcPublication = {
  id: string
  videoId: string
  platform: UgcPlatform
  url: string
  publishedOn: string
  readingDueOn: string
  status: PublicationStatus
  declaredViews: number | null
  hasScreenshot: boolean
  screenshotSubmittedAt: string | null
  validatedViews: number | null
  validatedAt: string | null
  readingSource: ReadingSource | null
  reviewComment: string | null
  /** Date du retour (capture refusée), affichée « Notre retour, le … ». */
  reviewCommentAt: string | null
  remindersSent: number
  createdAt: string
}

export type UgcPayout = {
  id: string
  videoId: string
  memberId: string
  month: string
  totalViews: number
  amountCents: number
  rateCentsPerThousand: number
  thresholdViews: number
  capViews: number
  overQuota: boolean
  settlementId: string | null
  frozenAt: string
}

export type UgcReferralBonus = {
  id: string
  referrerMemberId: string
  referredMemberId: string
  amountCents: number
  status: BonusStatus
  earnedPayoutId: string | null
  settlementId: string | null
  earnedAt: string | null
  createdAt: string
}

export type UgcSettlement = {
  id: string
  memberId: string | null
  kind: SettlementKind
  totalCents: number
  invoiceReceivedAt: string | null
  paidAt: string | null
  note: string | null
  expenseId: string | null
  createdAt: string
}

export type UgcSettings = {
  invoiceEmail: string | null
  alertEmail: string | null
  companyDetails: string | null
  updatedAt: string | null
}

export type UgcCounts = {
  applications: number
  ideas: number
  ideasNearDeadline: number
  drafts: number
  screenshots: number
  missingScreenshots: number
  settlementsToPay: number
}

// ── Vues du portail ──────────────────────────────────────────────────────────

/** Profil du créateur connecté et coordonnées utiles à sa facture (réglages du programme). */
export type PortalMe = {
  member: {
    id: string
    displayName: string
    email: string
    status: MemberStatus
    canInvoice: boolean
    referralCode: string
    needsConsent: boolean
    /** Vrai quand la personne a créé un mot de passe (connexion par e-mail et mot de passe). */
    hasPassword: boolean
    /** E-mails des messages de l'équipe : un à chaque message, ou un résumé chaque matin (page « E-mails »). */
    emailDigest: EmailDigest
    /** Pro offert : adresse du compte dans l'app, heure de la demande, dernier jour du Pro. */
    appAccount: { email: string | null; proRequestedAt: string | null; proUntil: string | null; proDismissedAt: string | null }
    /**
     * Son montant pour 1 000 vues, en centimes, et jamais celui d'une autre personne ; `null` tant
     * que l'équipe ne l'a pas fixé (aucun montant ne s'affiche alors dans l'espace).
     */
    rateCentsPerThousand: number | null
  }
  billing: { invoiceEmail: string | null; companyDetails: string | null }
}

/** Comptes du membre (page Profil), sans `@` : sa fiche de contact, à défaut sa candidature. */
export type PortalProfile = { tiktokHandle: string | null; instagramHandle: string | null }

/** Carte de « Mes vidéos » : dernier retour et nombre de publications. */
export type PortalVideoCard = { video: UgcVideo; lastReview: UgcReview | null; publicationCount: number }

/** Page d'une vidéo : retours dans l'ordre, versions envoyées, envoi en cours éventuel. */
export type PortalVideoDetail = {
  video: UgcVideo
  reviews: UgcReview[]
  drafts: UgcDraft[]
  uploadInProgress: UgcDraft | null
}

/** Élément payable (montant figé ou parrainage gagné) et son état de paiement. */
export type PayableItem = { kind: 'payout' | 'bonus'; id: string; label: string; amountCents: number; payState: PayState }

/**
 * Montant figé vu par le portail : sans montant ni tarif (`null`) tant que le montant pour 1 000
 * vues de la personne n'est pas fixé (le serveur ne les envoie pas, `maskPublicationsAmounts`).
 */
export type PortalPayout = Omit<UgcPayout, 'amountCents' | 'rateCentsPerThousand'> & {
  amountCents: number | null
  rateCentsPerThousand: number | null
  payState: PayState
  paidAt: string | null
}

/**
 * « Mes publications » : trois chiffres, une carte par vidéo publiée, détail de la facture (éléments
 * dus seulement). Tant que le montant pour 1 000 vues n'est pas fixé, `dueCents` et `paidCents` valent
 * `null`, chaque `payout.amountCents` aussi, et `invoiceLines` est vide.
 */
export type PortalPublicationsView = {
  summary: { dueCents: number | null; inProgress: number; paidCents: number | null }
  videos: {
    videoId: string
    title: string
    publications: UgcPublication[]
    payout: PortalPayout | null
    canAddPlatform: UgcPlatform | null
  }[]
  approvedWithoutPublication: { videoId: string; title: string }[]
  invoiceLines: { label: string; amountCents: number }[]
}

/** État d'un parrainage vu par le parrain : en attente de la première vidéo payée, gagné, payé. */
export type PortalReferralState = 'waiting' | 'earned' | 'paid'

/** « Parrainage » : lien et code, Gagné et Payé, personnes parrainées dont l'inscription est acceptée. */
export type PortalReferralsView = {
  code: string
  link: string
  /** `null` tant que le montant pour 1 000 vues du membre n'est pas fixé (aucun montant envoyé). */
  earnedCents: number | null
  paidCents: number | null
  referrals: { name: string; acceptedAt: string; state: PortalReferralState; paidAt: string | null }[]
}

// ── Vues de l'admin ──────────────────────────────────────────────────────────

/**
 * Ligne de la file des vidéos (ou de « Toutes les vidéos ») : `submittedAt` est l'entrée dans
 * l'état courant ; `ideaDeadline` = soumission + 48 h pour une idée à traiter, sinon `null` ;
 * `draftDeadline` = soumission + le repère de relecture d'un brouillon à traiter, sinon `null` ;
 * `unread` = messages du membre plus récents que la dernière ouverture du fil par l'équipe.
 */
export type AdminVideoQueueItem = {
  video: UgcVideo
  memberId: string
  memberName: string
  stage: ReviewStage
  draft: UgcDraft | null
  submittedAt: string
  ideaDeadline: string | null
  draftDeadline: string | null
  unread: number
}

/** Page d'une vidéo dans l'admin : retours dans l'ordre et versions reçues (effacées comprises). */
export type AdminVideoDetail = { video: UgcVideo; memberName: string; reviews: UgcReview[]; drafts: UgcDraft[] }

/**
 * Ligne de « Publications et paiements » (captures à valider, relevés à venir, toutes) :
 * `missing` = en ligne sans capture à partir du 37e jour (« Capture manquante ») ; `onlineUntil`
 * = dernier jour des 60 jours en ligne ; `payout` = montant figé de la vidéo, s'il existe ;
 * `memberRateCentsPerThousand` = montant pour 1 000 vues de la personne, `null` tant qu'il n'est
 * pas fixé (le relevé ne se valide pas).
 */
export type AdminPublicationItem = {
  publication: UgcPublication
  videoTitle: string
  memberId: string
  memberName: string
  memberCreatorId: string
  memberRateCentsPerThousand: number | null
  missing: boolean
  onlineUntil: string
  payout: UgcPayout | null
}

/**
 * « À payer » groupé par personne (éléments dus seulement, total), règlements en cours (facture
 * reçue, pas encore payée) et historique (payés ou réglés autrement). `memberName` vaut `null`
 * dans l'historique quand la personne a été effacée.
 */
export type AdminPaymentsView = {
  due: { memberId: string; memberName: string; canInvoice: boolean; items: PayableItem[]; totalCents: number }[]
  open: (UgcSettlement & { memberName: string })[]
  history: (UgcSettlement & { memberName: string | null })[]
}

/**
 * Ligne de l'onglet « Parrainages » : `acceptedAt` = acceptation de la personne parrainée
 * (création du membre) ; `payState` vaut `none` tant que le parrainage est en attente.
 */
export type AdminReferralItem = {
  bonus: UgcReferralBonus
  referrerName: string
  referredName: string
  acceptedAt: string
  payState: PayState
}

/**
 * Compteurs des onglets, du Cockpit et de l'e-mail du matin, plus les personnes à qui l'on doit
 * (`membersWithDue`) dont celles sans statut pour facturer, dont le règlement est à arranger
 * (`arrangementsDue`).
 */
/**
 * `ideasLate` et `draftsLate` : idées et brouillons dont l'échéance de réponse est passée
 * (functions/lib/ugc/replyDeadline.ts), signalés dans l'e-mail du matin.
 */
export type UgcOverview = UgcCounts & {
  membersWithDue: number
  arrangementsDue: number
  ideasLate: number
  draftsLate: number
  /** Demandes de Pro en attente (functions/lib/ugc/pro.ts, waitsForPro). */
  proRequests: number
  /** Membres partis dont le Pro court encore : à retirer dans RevenueCat. */
  proRemovals: number
  /** Messages des membres dans le chat avec l'équipe, plus récents que la dernière lecture de l'équipe. */
  chatUnread: number
  /** Membres actifs ou en pause dont le montant pour 1 000 vues n'est pas encore fixé (onglet « Montants à fixer »). */
  ratesToSet: number
}

/**
 * Ce qui attend une action de l'admin, par onglet (R44) : une personne qui facture et dont la
 * facture n'est pas arrivée ne compte pas ; seules comptent celles dont le règlement est à arranger.
 * Partagé par les onglets, le Cockpit et l'e-mail du matin du robot.
 */
/** Adresse du compte admin : e-mail du matin tant qu'aucune adresse n'est réglée (spec § 6.6). */
export const DEFAULT_ALERT_EMAIL = 'otia.ltd@gmail.com'

export function ugcActionCounts(o: UgcOverview): {
  applications: number
  videos: number
  payments: number
  members: number
  messages: number
  rates: number
  total: number
} {
  const videos = o.ideas + o.drafts
  const payments = o.screenshots + o.missingScreenshots + o.settlementsToPay + o.arrangementsDue
  // Pro à accorder dans RevenueCat (fiches des membres) : compté dans le total, donc dans l'e-mail du matin.
  const members = o.proRequests + o.proRemovals
  // Messages du chat pas encore lus par l'équipe (onglet « Messages ») : comptés dans le total aussi.
  const messages = o.chatUnread
  // Montants pour 1 000 vues à fixer (onglet « Montants à fixer ») : comptés dans le total aussi.
  // `?? 0` : un compteur d'avant la migration 0021 (cache, robot pas encore à jour) n'en a pas.
  const rates = o.ratesToSet ?? 0
  return {
    applications: o.applications,
    videos,
    payments,
    members,
    messages,
    rates,
    total: o.applications + videos + payments + members + messages + rates,
  }
}

// ── Conversation d'une vidéo ─────────────────────────────────────────────────

/** Auteur d'un message du fil : la personne du programme ou l'équipe. */
export type MessageAuthor = 'member' | 'team'

/** E-mails des messages de l'équipe : un à chaque message, ou un résumé chaque matin (`daily`). */
export type EmailDigest = 'each' | 'daily'

export type UgcMessage = { id: string; videoId: string; author: MessageAuthor; body: string; createdAt: string }

/**
 * Sort de l'e-mail d'un message : parti (`sent`) ; retenu parce qu'un e-mail est déjà parti sur cette
 * vidéo il y a moins de `UGC_RULES.messageEmailWindowMinutes` minutes (`recent`) ou parce que le
 * plafond du jour est atteint (`daily_cap`) ; en échec (`failed`) ; ou gardé pour le résumé du
 * matin, s'il n'est pas lu d'ici là, la personne ayant choisi un résumé par jour (`digest`, message
 * de l'équipe seulement).
 */
export type ConversationEmailOutcome = 'sent' | 'recent' | 'daily_cap' | 'failed' | 'digest'

/** Élément du fil d'une vidéo, construit par `functions/lib/ugc/thread.ts` (portail et admin). */
export type { ThreadItem }

/** Carte de « Mes vidéos » avec le nombre de messages de l'équipe pas encore lus (« 1 nouveau »). */
export type PortalVideoListItem = PortalVideoCard & { unread: number }

/**
 * Conversation d'une vidéo dans le portail (`GET /api/portal/videos/:id/thread`) : la vidéo, son
 * fil dans l'ordre, l'envoi de brouillon en cours (reprise), les messages de l'équipe non lus avant
 * cette ouverture et la lecture précédente (pour placer « Nouveau »), et `canWrite`, faux pour une
 * vidéo refusée (conversation fermée côté membre).
 */
export type PortalVideoThread = {
  video: UgcVideo
  items: ThreadItem[]
  uploadInProgress: UgcDraft | null
  unread: number
  lastReadAt: string | null
  canWrite: boolean
}

/**
 * Conversation d'une vidéo dans l'admin (`GET /api/ugc/videos/:id/thread`) : comme le portail,
 * avec le nom de la personne ; `canWrite` est faux quand elle a quitté le programme.
 */
export type AdminVideoThread = {
  video: UgcVideo
  memberName: string
  items: ThreadItem[]
  unread: number
  lastReadAt: string | null
  /**
   * Lecture du fil par la personne (`member_read_at`), pour « Lu » sous notre dernier message :
   * admin seulement, le portail ne montre jamais de « lu ».
   */
  memberReadAt: string | null
  canWrite: boolean
}

// ── Chat avec l'équipe ───────────────────────────────────────────────────────

/** Message du chat avec l'équipe : une conversation par membre, en dehors de toute vidéo. */
export type UgcChatMessage = { id: string; memberId: string; author: MessageAuthor; body: string; createdAt: string }

/**
 * Chat avec l'équipe dans le portail (`GET /api/portal/chat`) : les messages dans l'ordre, les
 * messages de l'équipe non lus avant cette ouverture et la lecture précédente (pour placer
 * « Nouveau »). Ouvrir le chat le marque lu côté membre.
 */
export type PortalChatThread = {
  messages: UgcChatMessage[]
  unread: number
  lastReadAt: string | null
}

/**
 * Non-lus du portail (`GET /api/portal/unread`) : messages de l'équipe pas encore lus, toutes
 * vidéos (pastille de l'onglet Vidéos), et dans le chat (pastille de l'onglet Équipe).
 */
export type PortalUnread = { videos: number; chat: number }

/**
 * Ligne de l'onglet « Messages » de l'admin (`GET /api/ugc/chats`) : un membre dont le chat a au
 * moins un message, le dernier message et les messages du membre plus récents que la dernière
 * ouverture du chat par l'équipe.
 */
export type AdminChatListItem = {
  memberId: string
  memberName: string
  memberStatus: MemberStatus
  lastMessage: { author: MessageAuthor; body: string; createdAt: string }
  unread: number
}

/**
 * Chat d'un membre dans l'admin (`GET /api/ugc/chats/:memberId`) : comme le portail, avec le nom et
 * l'état de la personne ; `canWrite` est faux quand elle n'est pas (ou plus) dans le programme
 * (invitée qui n'a pas encore accepté les conditions, ou partie).
 */
export type AdminChatThread = {
  memberId: string
  /** Fiche de la personne dans le CRM (`/crm/contacts/:creatorId`). */
  creatorId: string
  memberName: string
  memberStatus: MemberStatus
  emailDigest: EmailDigest
  messages: UgcChatMessage[]
  unread: number
  lastReadAt: string | null
  /** Lecture du chat par la personne (`chat_member_read_at`), pour « Lu » : admin seulement. */
  memberReadAt: string | null
  canWrite: boolean
}
